import apiClient from "./api";

export type CustomerImportAction = "Create" | "Update" | "Skip";

export interface CustomerImportRowResult {
  rowNumber: number;
  customerId: string;
  action: CustomerImportAction;
  errors: string[];
  warnings: string[];
  resolvedCustomerCompanyId?: string | null;
  resolvedCustomerCompanyName?: string | null;
  resolvedRole: number;
  resolvedCredentialType: number;
}

export interface CustomerImportPreviewResult {
  totalRows: number;
  creates: number;
  updates: number;
  skips: number;
  errors: number;
  rows: CustomerImportRowResult[];
  newCustomerCompanies: string[];
}

export interface CustomerImportCommitResult {
  created: number;
  updated: number;
  skipped: number;
  errored: number;
  rows: CustomerImportRowResult[];
}

/**
 * Per-upload caps, returned by `GET /customer-import/limits`. A request over
 * either is refused with 413 (`importFileTooLarge` / `importTooManyRows`).
 */
export interface CustomerImportLimits {
  maxRows: number;
  maxFileBytes: number;
}

/**
 * Server eligible for credential pre-fill in the template. Returned by
 * `GET /customer-import/eligible-servers`.
 */
export interface CustomerImportEligibleServer {
  id: string;
  name: string;
  authorizationType: string;
  headerNames: string[];
}

const actionNames: CustomerImportAction[] = ["Create", "Update", "Skip"];

async function postCsv<T extends { rows: CustomerImportRowResult[] }>(
  path: string,
  file: File,
  updateExisting: boolean,
): Promise<T> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("updateExisting", String(updateExisting));
  const response = await apiClient.post(path, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const data = response.data as T;
  return {
    ...data,
    rows: data.rows.map((row) => {
      const action = row.action as CustomerImportAction | number;
      return {
        ...row,
        action: typeof action === "number" ? actionNames[action] : action,
      };
    }),
  };
}

function triggerBrowserDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const customerImportService = {
  /**
   * `updateExisting: false` reports rows for customers that already exist as
   * Skip and leaves them unchanged on commit.
   */
  preview: (
    file: File,
    updateExisting: boolean,
  ): Promise<CustomerImportPreviewResult> =>
    postCsv("/customer-import/preview", file, updateExisting),

  commit: (
    file: File,
    updateExisting: boolean,
  ): Promise<CustomerImportCommitResult> =>
    postCsv("/customer-import/commit", file, updateExisting),

  getLimits: (): Promise<CustomerImportLimits> =>
    apiClient.get("/customer-import/limits").then((r) => r.data),

  listEligibleServers: (): Promise<CustomerImportEligibleServer[]> =>
    apiClient.get("/customer-import/eligible-servers").then((r) => r.data),

  /**
   * Downloads the import template. When `serverIds` is non-empty, the template
   * pre-fills an example `ServerAuthorizationsJson` cell with entries for each
   * selected server (header names + format-aware placeholders).
   */
  downloadTemplate: async (serverIds: string[]): Promise<void> => {
    const response = await apiClient.post(
      "/customer-import/template",
      { serverIds },
      { responseType: "blob" },
    );
    triggerBrowserDownload(
      response.data as Blob,
      "customers-import-template.csv",
    );
  },
};
